/**
 * Module Store
 *
 * Zustand store managing the module lifecycle: install, activate,
 * deactivate, uninstall. Handles content injection into bestiary
 * and session stores, command registration, and event subscriptions.
 */

import { create } from 'zustand';
import type {
  MachinaModule,
  ActiveModule,
  BestiaryEntry,
  SessionPlan,
  ModuleEventType,
  ModuleAPI,
  ModuleAPIProviders,
  BestiaryView,
  CombatantView,
  CharacterView,
  SceneView,
} from '@machina/core';
import {
  validateModule,
  moduleNamespace,
  namespacedId,
  resolveModuleDependencies,
  registerCommand,
  unregisterModuleCommands,
  subscribeModuleEvent,
  clearModuleSubscriptions,
  emitModuleEvent,
  setActiveSystem,
  createModuleAPI,
  compileCommandHandler,
  compileEventHandler,
  composeSystemRules,
  getSystem,
  registerSystem,
  rollDice,
  parseDiceExpression,
} from '@machina/core';
import { useCombatStore } from './combatStore';
import { useBestiaryStore } from './bestiaryStore';
import { useSessionPlanStore } from './sessionPlanStore';
import { useChatStore } from './chatStore';
import { useCharacterStore } from './characterStore';
import { useEncounterStore } from './encounterStore';
import { useTokenStore } from './tokenStore';
import { useIsoSpriteStore } from './isoSpriteStore';
import type { SpriteFacing } from './isoSpriteStore';
import { useCampaignStore } from './campaignStore';

// =============================================================================
// PERSISTENCE HELPERS
// =============================================================================

function persistInstalled(installed: Map<string, MachinaModule>) {
  localStorage.setItem('machina:installed-modules', JSON.stringify(Array.from(installed.entries())));
}

function persistActive(campaignId: string, active: Map<string, ActiveModule>) {
  const ids = Array.from(active.keys());
  localStorage.setItem(`machina:active-modules:${campaignId}`, JSON.stringify(ids));
}

// =============================================================================
// MODULE API FACTORY — builds the safe API object for handler execution
// =============================================================================

/** Per-module localStorage storage namespace */
function moduleStorageKey(moduleId: string, key: string): string {
  return `machina:mod-storage:${moduleId}:${key}`;
}

function buildModuleAPI(mod: MachinaModule, campaignId: string): ModuleAPI {
  const campaign = useCampaignStore.getState().campaigns.find((c) => c.id === campaignId);
  const providers: ModuleAPIProviders = {
    moduleId: mod.id,
    moduleName: mod.name,
    campaignId,
    playset: campaign?.playset ?? null,
    systemId: mod.system || 'dnd-5e',

    getBestiary: (): BestiaryView[] => {
      const entries = useBestiaryStore.getState().entries;
      return entries.map((e) => ({
        id: e.id,
        name: e.name,
        type: e.creatureType || 'unknown',
        ac: e.ac,
        maxHp: e.maxHp,
        challenge: e.cr ?? undefined,
        abilities: e.abilities,
        source: e.source ?? undefined,
      }));
    },

    getCreature: (name: string): BestiaryView | undefined => {
      const lower = name.toLowerCase();
      const entry = useBestiaryStore.getState().entries.find(
        (e) => e.name.toLowerCase() === lower
      );
      if (!entry) return undefined;
      return {
        id: entry.id,
        name: entry.name,
        type: entry.creatureType || 'unknown',
        ac: entry.ac,
        maxHp: entry.maxHp,
        challenge: entry.cr ?? undefined,
        abilities: entry.abilities,
        source: entry.source ?? undefined,
      };
    },

    isInCombat: () => useCombatStore.getState().inCombat,

    getCombatants: (): CombatantView[] => {
      const enc = useEncounterStore.getState().activeEncounter;
      if (!enc) return [];
      return Array.from(enc.participants.values()).map((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
        initiative: p.initiative,
        isAlive: p.isAlive,
      }));
    },

    getCurrentTurn: (): CombatantView | undefined => {
      const enc = useEncounterStore.getState().activeEncounter;
      if (!enc || !enc.currentTurnId) return undefined;
      const p = enc.participants.get(enc.currentTurnId);
      if (!p) return undefined;
      return { id: p.id, name: p.name, type: p.type, initiative: p.initiative, isAlive: p.isAlive };
    },

    getRound: () => {
      const enc = useEncounterStore.getState().activeEncounter;
      return enc?.round ?? 0;
    },

    getCharacters: (): CharacterView[] => {
      const chars = useCharacterStore.getState().characters;
      const result: CharacterView[] = [];
      chars.forEach((c) => {
        const totalLevel = c.classes.reduce((sum, cls) => sum + cls.level, 0);
        result.push({
          id: c.id,
          name: c.name,
          level: totalLevel || 1,
          className: c.classes[0]?.name ?? 'Unknown',
          hp: c.currentHp,
          maxHp: c.maxHp,
          ac: c.armorClass,
          abilities: {
            str: c.abilityScores.strength,
            dex: c.abilityScores.dexterity,
            con: c.abilityScores.constitution,
            int: c.abilityScores.intelligence,
            wis: c.abilityScores.wisdom,
            cha: c.abilityScores.charisma,
          },
        });
      });
      return result;
    },

    getCharacter: (name: string): CharacterView | undefined => {
      const lower = name.toLowerCase();
      let found: CharacterView | undefined;
      useCharacterStore.getState().characters.forEach((c) => {
        if (c.name.toLowerCase() === lower) {
          const totalLevel = c.classes.reduce((sum, cls) => sum + cls.level, 0);
          found = {
            id: c.id,
            name: c.name,
            level: totalLevel || 1,
            className: c.classes[0]?.name ?? 'Unknown',
            hp: c.currentHp,
            maxHp: c.maxHp,
            ac: c.armorClass,
            abilities: {
              str: c.abilityScores.strength,
              dex: c.abilityScores.dexterity,
              con: c.abilityScores.constitution,
              int: c.abilityScores.intelligence,
              wis: c.abilityScores.wisdom,
              cha: c.abilityScores.charisma,
            },
          };
        }
      });
      return found;
    },

    getActiveScene: (): SceneView | undefined => {
      const state = useSessionPlanStore.getState();
      const session = state.activeSessionId ? state.sessions.get(state.activeSessionId) : undefined;
      if (!session) return undefined;
      const scene = session.scenes[session.activeSceneIndex];
      if (!scene) return undefined;
      return { id: scene.id, name: scene.name, location: scene.location, status: scene.status };
    },

    sendSystemMessage: (text: string) => {
      useChatStore.getState().addSystemMessage(text);
    },

    sendNpcMessage: (npcName: string, text: string) => {
      useChatStore.getState().addNpcMessage({
        npcId: `mod:${mod.id}/${npcName}`,
        npcName,
        message: text,
      });
    },

    emit: (event: string, payload?: Record<string, unknown>) => {
      emitModuleEvent(event as ModuleEventType, { ...payload, sourceModule: mod.id }).catch((err) => {
        console.error(`[moduleStore] Error emitting ${event} from ${mod.id}:`, err);
      });
    },

    roll: (expression: string): number => {
      return rollDice(expression).total;
    },

    rollDetailed: (expression: string) => {
      const result = rollDice(expression);
      const flatRolls = result.rolls.flat();
      const parsed = parseDiceExpression(expression);
      const isD20 = parsed.sides === 20 && parsed.count === 1;
      const natural = isD20 ? flatRolls[0] : undefined;
      return {
        total: result.total,
        rolls: flatRolls,
        modifier: result.modifier,
        formula: expression,
        natural20: natural === 20,
        natural1: natural === 1,
      };
    },

    getStorage: (key: string): unknown => {
      const raw = localStorage.getItem(moduleStorageKey(mod.id, key));
      if (raw === null) return undefined;
      try { return JSON.parse(raw); } catch { return raw; }
    },

    setStorage: (key: string, value: unknown) => {
      localStorage.setItem(moduleStorageKey(mod.id, key), JSON.stringify(value));
    },

    // Token providers
    getTokens: () => {
      const tokens = useTokenStore.getState().tokens;
      return Array.from(tokens.values()).map((t) => ({
        id: t.id,
        name: t.name,
        position: { x: t.position.x, y: t.position.y },
        size: t.visuals?.size || 'medium',
        color: t.visuals?.color || '#d4af37',
        imageUrl: t.visuals?.imageUrl,
        currentHp: t.currentHp,
        maxHp: t.maxHp,
        ac: t.ac,
        isPlayerControlled: t.isPlayerControlled,
        emitsLight: t.emitsLight,
      }));
    },

    getToken: (name: string) => {
      const lower = name.toLowerCase();
      const tokens = useTokenStore.getState().tokens;
      for (const t of tokens.values()) {
        if (t.name.toLowerCase() === lower) {
          return {
            id: t.id,
            name: t.name,
            position: { x: t.position.x, y: t.position.y },
            size: t.visuals?.size || 'medium',
            color: t.visuals?.color || '#d4af37',
            imageUrl: t.visuals?.imageUrl,
            currentHp: t.currentHp,
            maxHp: t.maxHp,
            ac: t.ac,
            isPlayerControlled: t.isPlayerControlled,
            emitsLight: t.emitsLight,
          };
        }
      }
      return undefined;
    },

    addToken: (params) => {
      const token = useTokenStore.getState().addToken({
        name: params.name,
        position: params.position,
        visuals: {
          shape: 'circle',
          size: params.size || 'medium',
          color: params.color || '#d4af37',
          imageUrl: params.imageUrl,
          showHpBar: params.maxHp !== undefined,
          showConditionIcons: true,
        },
        currentHp: params.currentHp,
        maxHp: params.maxHp,
        ac: params.ac,
        emitsLight: params.emitsLight as 'torch' | 'lantern' | 'candle' | 'campfire' | undefined,
      });
      return token.id;
    },

    removeToken: (id: string) => {
      useTokenStore.getState().removeToken(id);
    },

    moveToken: (id: string, position: { x: number; y: number }) => {
      useTokenStore.getState().moveToken(id, position, true);
    },

    // Sprite providers — module API still addresses sprites by name (string id);
    // the iso sprite store is UUID-keyed per-instance internally, so for module
    // entities we use the module-supplied name as the stable id.
    generateSprite: (params) => {
      useIsoSpriteStore.getState().generateMissing([{
        id: params.name,
        name: params.name,
        race: params.race,
        className: params.className,
        appearance: params.appearance,
        equipment: params.equipment,
      }]);
    },

    setSprite: (name: string, imageUrl: string, facing?: SpriteFacing) => {
      useIsoSpriteStore.getState().setCustomSprite(
        name,
        imageUrl,
        facing || 'south',
        name,
      );
    },

    getSpriteCount: (name: string) => {
      return useIsoSpriteStore.getState().facingCount(name);
    },

    setSpriteSeed: (name: string, imageUrl: string) => {
      useIsoSpriteStore.getState().setSeed(name, imageUrl);
    },

    getSpriteSeed: (name: string) => {
      return useIsoSpriteStore.getState().getSeed(name);
    },
  };

  return createModuleAPI(providers);
}

// =============================================================================
// STORE
// =============================================================================

interface ModuleState {
  installed: Map<string, MachinaModule>;
  active: Map<string, ActiveModule>;
  _campaignId: string | null;
  isLoading: boolean;
  loadError: string | null;

  // Actions
  installModule: (moduleJson: unknown) => Promise<{ success: boolean; errors?: string[] }>;
  activateModule: (moduleId: string, campaignId: string) => Promise<void>;
  deactivateModule: (moduleId: string) => void;
  uninstallModule: (moduleId: string) => void;
  getActiveModules: () => ActiveModule[];
  isActive: (moduleId: string) => boolean;
  loadModulesForCampaign: (campaignId: string) => Promise<void>;
}

export const useModuleStore = create<ModuleState>((set, get) => ({
  installed: new Map(),
  active: new Map(),
  _campaignId: null,
  isLoading: false,
  loadError: null,

  // ---------------------------------------------------------------------------
  // Install — validate and store
  // ---------------------------------------------------------------------------
  installModule: async (moduleJson) => {
    set({ loadError: null });

    // Unwrap .machina bundle format: { _machina, module } → module
    let rawModule = moduleJson;
    if (rawModule && typeof rawModule === 'object' && 'module' in rawModule) {
      rawModule = (rawModule as Record<string, unknown>).module;
    }

    const result = validateModule(rawModule);
    if (!result.success) {
      const errors = result.errors;
      set({ loadError: errors.join('; ') });
      return { success: false, errors };
    }

    const mod = result.data;

    // Check for duplicate
    const { installed } = get();
    if (installed.has(mod.id)) {
      const msg = `Module "${mod.id}" is already installed`;
      set({ loadError: msg });
      return { success: false, errors: [msg] };
    }

    set((s) => {
      const next = new Map(s.installed);
      next.set(mod.id, mod);
      persistInstalled(next);
      return { installed: next, loadError: null };
    });

    console.log(`[moduleStore] Installed module: ${mod.name} (${mod.id})`);
    return { success: true };
  },

  // ---------------------------------------------------------------------------
  // Activate — inject content, register commands/events
  // ---------------------------------------------------------------------------
  activateModule: async (moduleId, campaignId) => {
    const { installed, active } = get();
    const mod = installed.get(moduleId);
    if (!mod) throw new Error(`Module "${moduleId}" is not installed`);
    if (active.has(moduleId)) return; // already active

    // Track campaign context for persistence
    set({ _campaignId: campaignId });

    // Check dependencies
    const deps = resolveModuleDependencies(mod, installed);
    if (!deps.resolved) {
      throw new Error(`Missing dependencies: ${deps.missing.join(', ')}`);
    }

    // Guard: cannot hot-swap game system during active combat
    if (mod.type === 'game-system' && useCombatStore.getState().inCombat) {
      throw new Error('Cannot switch game system during active combat');
    }

    const ns = moduleNamespace(moduleId);

    // Build ActiveModule
    const activeModule: ActiveModule = {
      module: mod,
      status: 'loading',
      activatedAt: new Date().toISOString(),
      namespace: ns,
    };

    set((s) => {
      const next = new Map(s.active);
      next.set(moduleId, activeModule);
      return { active: next };
    });

    try {
      // --- Inject bestiary entries ---
      if (mod.bestiary && mod.bestiary.length > 0) {
        const entries: BestiaryEntry[] = mod.bestiary.map((raw) => ({
          ...(raw as unknown as BestiaryEntry),
          id: namespacedId(moduleId, (raw as Record<string, unknown>).id as string ?? crypto.randomUUID()),
          campaignId,
          source: ns,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
        useBestiaryStore.getState().injectModuleEntries(entries);
      }

      // --- Inject sessions ---
      if (mod.sessions && mod.sessions.length > 0) {
        const sessions: SessionPlan[] = mod.sessions.map((raw) => {
          const r = raw as Record<string, unknown>;
          const rawScenes = (r.scenes as Array<Record<string, unknown>>) ?? [];
          // Normalize scenes: module format may lack name/description/durationMinutes
          const scenes = rawScenes.map((s) => ({
            id: s.id as string ?? crypto.randomUUID(),
            name: (s.name as string) ?? (s.location as string) ?? 'Unnamed Scene',
            location: (s.location as string) ?? '',
            durationMinutes: (s.durationMinutes as [number, number]) ?? [10, 20],
            description: (s.description as string) ?? (s.notes as string) ?? '',
            goal: s.goal as string | undefined,
            backdrop: s.backdrop as SessionPlan['scenes'][0]['backdrop'],
            skillChecks: (s.skillChecks as SessionPlan['scenes'][0]['skillChecks']) ?? [],
            encounter: s.encounter as SessionPlan['scenes'][0]['encounter'],
            chase: s.chase as SessionPlan['scenes'][0]['chase'],
            clueCardIds: (s.clueCardIds as string[]) ?? [],
            notes: s.notes as string | undefined,
            status: (s.status as 'upcoming' | 'active' | 'completed') ?? 'upcoming',
          }));
          return {
            id: namespacedId(moduleId, r.id as string ?? crypto.randomUUID()),
            campaignId,
            name: (r.name as string) ?? mod.name,
            subtitle: r.subtitle as string | undefined,
            tone: (r.tone as string) ?? '',
            lengthMinutes: (r.lengthMinutes as [number, number]) ?? [60, 120],
            coreLoop: (r.coreLoop as string) ?? '',
            truth: (r.truth as string) ?? '',
            scenes,
            clueCards: (r.clueCards as SessionPlan['clueCards']) ?? [],
            npcs: (r.npcs as SessionPlan['npcs']) ?? [],
            optionalEncounters: r.optionalEncounters as SessionPlan['optionalEncounters'],
            activeSceneIndex: (r.activeSceneIndex as number) ?? 0,
            status: (r.status as 'prep' | 'running' | 'completed') ?? 'prep',
            createdAt: new Date(),
            updatedAt: new Date(),
          } satisfies SessionPlan;
        });
        useSessionPlanStore.getState().injectModuleSessions(sessions);
      }

      // --- Build Module API for handler execution ---
      const api = buildModuleAPI(mod, campaignId);

      // --- Register commands ---
      if (mod.customCommands && mod.customCommands.length > 0) {
        for (const cmd of mod.customCommands) {
          const body = cmd.handlerBody;
          if (body) {
            // Compile inline handler body into real executable function
            const compiled = compileCommandHandler(body, moduleId);
            registerCommand({
              name: cmd.name,
              description: cmd.description,
              usage: cmd.usage,
              moduleId,
              handler: (ctx) => compiled(api, ctx),
            });
          } else {
            // No handler body — register placeholder
            registerCommand({
              name: cmd.name,
              description: cmd.description,
              usage: cmd.usage,
              moduleId,
              handler: () => `[${mod.name}] Command /${cmd.name} has no handler body`,
            });
          }
        }
      }

      // --- Subscribe event handlers ---
      if (mod.eventHandlers && mod.eventHandlers.length > 0) {
        for (const eh of mod.eventHandlers) {
          const body = eh.handlerBody;
          if (body) {
            const compiled = compileEventHandler(body, moduleId);
            subscribeModuleEvent({
              moduleId,
              event: eh.event as ModuleEventType,
              priority: eh.priority,
              handler: () => compiled(api, {}),
            });
          } else {
            subscribeModuleEvent({
              moduleId,
              event: eh.event as ModuleEventType,
              priority: eh.priority,
              handler: () => {
                console.log(`[moduleStore] Event "${eh.event}" fired (${mod.name}) — no handler body`);
              },
            });
          }
        }
      }

      // --- Activate game system (with extends: composition) ---
      if (mod.type === 'game-system' && mod.rules) {
        if (mod.extends) {
          // Compose child rules on top of parent system
          const parentAdapter = getSystem(mod.extends);
          if (parentAdapter) {
            const composedRules = composeSystemRules(parentAdapter.rules, mod.rules);
            // Register as a new composed adapter that delegates methods to parent
            registerSystem({
              ...parentAdapter,
              id: mod.system,
              name: composedRules.name,
              version: mod.version,
              rules: composedRules,
              getDmGuidance: () => composedRules.dmGuidance ?? parentAdapter.getDmGuidance(),
              getAbilities: () => composedRules.abilities.map((a) => a),
              getClasses: () => composedRules.classes?.map((c) => c) ?? parentAdapter.getClasses(),
              getConditions: () => composedRules.conditions?.map((c) => c) ?? parentAdapter.getConditions(),
            });
            console.log(`[moduleStore] Composed system "${mod.system}" extending "${mod.extends}"`);
          } else {
            console.warn(`[moduleStore] Parent system "${mod.extends}" not found — activating without composition`);
          }
        }
        setActiveSystem(mod.system);
      }

      // Mark as active
      set((s) => {
        const next = new Map(s.active);
        next.set(moduleId, { ...activeModule, status: 'active' });
        persistActive(campaignId, next);
        return { active: next };
      });

      await emitModuleEvent('module_activated', { moduleId, campaignId });
      console.log(`[moduleStore] Activated module: ${mod.name}`);
    } catch (err) {
      // Mark as error
      set((s) => {
        const next = new Map(s.active);
        next.set(moduleId, {
          ...activeModule,
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
        return { active: next };
      });
      throw err;
    }
  },

  // ---------------------------------------------------------------------------
  // Deactivate — remove injected content, unregister commands/events
  // ---------------------------------------------------------------------------
  deactivateModule: (moduleId) => {
    const { active, installed } = get();
    if (!active.has(moduleId)) return;

    const ns = moduleNamespace(moduleId);
    const mod = installed.get(moduleId);

    // Reset game system to default if this was the active game-system module
    if (mod && mod.type === 'game-system') {
      setActiveSystem('dnd-5e');
    }

    // Remove bestiary entries with matching source
    useBestiaryStore.getState().removeModuleEntries(ns);

    // Remove module sessions
    useSessionPlanStore.getState().removeModuleSessions(ns);

    // Unregister commands
    unregisterModuleCommands(moduleId);

    // Clear event subscriptions
    clearModuleSubscriptions(moduleId);

    set((s) => {
      const next = new Map(s.active);
      next.delete(moduleId);
      const cid = s._campaignId;
      if (cid) persistActive(cid, next);
      return { active: next };
    });

    emitModuleEvent('module_deactivated', { moduleId }).catch((err) => {
      console.error('[moduleStore] Error emitting module_deactivated:', err);
    });

    console.log(`[moduleStore] Deactivated module: ${moduleId}`);
  },

  // ---------------------------------------------------------------------------
  // Uninstall — deactivate + remove
  // ---------------------------------------------------------------------------
  uninstallModule: (moduleId) => {
    const { active } = get();
    if (active.has(moduleId)) {
      get().deactivateModule(moduleId);
    }

    set((s) => {
      const next = new Map(s.installed);
      next.delete(moduleId);
      persistInstalled(next);
      return { installed: next };
    });

    console.log(`[moduleStore] Uninstalled module: ${moduleId}`);
  },

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------
  getActiveModules: () => Array.from(get().active.values()),

  isActive: (moduleId) => get().active.has(moduleId),

  // ---------------------------------------------------------------------------
  // Load from campaign settings (for reconnect / page reload)
  // ---------------------------------------------------------------------------
  loadModulesForCampaign: async (campaignId) => {
    set({ isLoading: true, loadError: null, _campaignId: campaignId });

    try {
      // Restore installed modules from localStorage
      const installedRaw = localStorage.getItem('machina:installed-modules');
      if (installedRaw) {
        const entries: Array<[string, MachinaModule]> = JSON.parse(installedRaw);
        const installedMap = new Map(entries);
        set({ installed: installedMap });
      }

      // Restore active module IDs for this campaign
      const activeRaw = localStorage.getItem(`machina:active-modules:${campaignId}`);
      if (activeRaw) {
        const activeIds: string[] = JSON.parse(activeRaw);
        for (const moduleId of activeIds) {
          if (get().installed.has(moduleId) && !get().active.has(moduleId)) {
            await get().activateModule(moduleId, campaignId);
          }
        }
      }

      set({ isLoading: false });
    } catch (err) {
      console.error('[moduleStore] Failed to load modules for campaign:', err);
      set({
        isLoading: false,
        loadError: err instanceof Error ? err.message : String(err),
      });
    }
  },
}));
